/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';
import { noop } from 'lodash';

import {
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
   StyledFlex,
} from '@dreryk/react-components';
import { TranslatableText, Translate } from '@dreryk/react-common';

import {
   AppointmentAdvancePaymentEnableCustomRefundActionEventInterceptor,
   AppointmentAdvancePaymentPaidView,
   AppointmentAdvancePaymentUnpaidView,
   isAutomaticRefundValid,
} from '~libs.desktop.shared.appointment-advance-payment';
import { RefundAdvancePaymentShowModalEventInterceptor } from '~libs.desktop.shared.refund-advance-payment';
import { visitCancelContentVisitor } from '~libs.shared.visit-cancel';
import { appointmentAdvancePaymentAutomaticRefundVisitor } from '~libs.shared.appointment-advance-payment';
import {
   AppointmentBeginDateTextView,
   AppointmentPatientTextView,
   AppointmentPractitionerTextView,
} from '~libs.desktop.shared.appointment';

import { translationKeys } from '../translations';

import { AppointmentCancelActionEventInterceptor } from './AppointmentCancelActionEventInterceptor';
import {
   performAppointmentAdvancePaymentRefundCqrsFind,
   performAppointmentDetailsCqrsGet,
   performRefundConfigurationCqrsFind,
   performVisitSettlementCqrsFind,
} from './VisitCancelShowModalEventInterceptor.utils';

type VisitCancelShowModalEventInterceptorProps = Readonly<{
   appointmentId: number;
   cancellable: boolean;
   children: JSX.Element;
   onSuccess: VoidFunction;
   onFault: VoidFunction;
}>;

export const VisitCancelShowModalEventInterceptor = (props: VisitCancelShowModalEventInterceptorProps): JSX.Element => {
   const handleRefundSuccess = useCallback(
      (resolve: VoidFunction): void => {
         props.onSuccess();
         return resolve();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.onSuccess]
   );

   const handleRefundCancel = useCallback(
      (resolve: VoidFunction): void => {
         props.onSuccess();
         return resolve();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.onSuccess]
   );

   const handleAppointmentCancelSuccess = useCallback(
      (snackbarMessage: TranslatableText, onSuccess?: VoidFunction): VoidFunction => {
         return () => {
            dispatchSnackbarPositiveEvent(snackbarMessage);
            onSuccess?.();
         };
      },
      []
   );

   const shouldShowModal = useCallback(async () => {
      if (!props.cancellable) {
         return false;
      }

      const [appointmentDetails, refundConfiguration, visitSettlement] = await Promise.all([
         performAppointmentDetailsCqrsGet(props.appointmentId),
         performRefundConfigurationCqrsFind(),
         performVisitSettlementCqrsFind(props.appointmentId),
      ]);

      const appointmentAdvancePaymentRefund = await performAppointmentAdvancePaymentRefundCqrsFind({
         appointmentId: props.appointmentId,
         automaticRefundEnabled: Boolean(refundConfiguration?.automaticRefundEnabled),
      });

      return {
         appointmentDetails: appointmentDetails,
         appointmentAdvancePaymentRefund: appointmentAdvancePaymentRefund,
         automaticRefundEnabled: Boolean(refundConfiguration?.automaticRefundEnabled),
         visitSettlement: visitSettlement,
      };
   }, [props.appointmentId, props.cancellable]);

   return (
      <ShowModalEventInterceptor
         shouldShowModal={shouldShowModal}
         testId={'visitCancelShowModal'}
         title={({ result: { visitSettlement } }) =>
            visitCancelContentVisitor(visitSettlement, {
               visitCancel: () => translationKeys.youAreCancellingVisit,
               visitCancelPaidAdvancePayment: () => translationKeys.youAreCancellingVisitWithAdvancePayment,
               visitCancelUnpaidAdvancePayment: () => translationKeys.youAreCancellingVisitWithAdvancePayment,
            })
         }
         footerItemsHidden
         modalWidth={ModalWidth.NORMAL}
         content={({
            resolve,
            reject,
            result: { appointmentDetails, visitSettlement, automaticRefundEnabled, appointmentAdvancePaymentRefund },
         }) => {
            const appointmentCancelSnackbarMessage = isAutomaticRefundValid(
               appointmentAdvancePaymentRefund?.refundExpirationDate
            )
               ? translationKeys.successfullyCancelledPlannedVisitAndRefundSuccessfullyCompleted
               : translationKeys.successfullyCancelledPlannedVisit;

            return (
               <StyledFlex gap={20} direction={'column'}>
                  <Translate>
                     {visitCancelContentVisitor(visitSettlement, {
                        visitCancel: () => translationKeys.areYouSureYouWantCancelVisit,
                        visitCancelPaidAdvancePayment: () =>
                           translationKeys.areYouSureYouWantCancelVisitWithAdvancePayment,
                        visitCancelUnpaidAdvancePayment: () => translationKeys.areYouSureYouWantCancelVisit,
                     })}
                  </Translate>

                  {visitCancelContentVisitor(visitSettlement, {
                     visitCancelPaidAdvancePayment: (advancePayment) => (
                        <>
                           <AppointmentAdvancePaymentPaidView
                              automaticRefundEnabled={automaticRefundEnabled}
                              appointmentAdvancePaymentRefund={appointmentAdvancePaymentRefund}
                              advancePayment={advancePayment}
                              appointmentDetails={appointmentDetails}
                           />

                           {appointmentAdvancePaymentAutomaticRefundVisitor(
                              {
                                 automaticRefundEnabled: automaticRefundEnabled,
                                 appointmentAdvancePaymentRefund: appointmentAdvancePaymentRefund,
                              },
                              {
                                 visitWithAutomaticRefund: () => (
                                    <>
                                       <AppointmentAdvancePaymentEnableCustomRefundActionEventInterceptor
                                          appointmentId={props.appointmentId}
                                       >
                                          <AppointmentCancelActionEventInterceptor
                                             appointmentId={props.appointmentId}
                                             onSuccess={handleAppointmentCancelSuccess(
                                                translationKeys.successfullyCancelledPlannedVisit
                                             )}
                                             onFault={props.onFault}
                                          >
                                             <RefundAdvancePaymentShowModalEventInterceptor
                                                advancePayment={advancePayment}
                                                onSuccess={() => handleRefundSuccess(resolve)}
                                                onCancel={() => handleRefundCancel(resolve)}
                                                beginDate={appointmentDetails.beginDate}
                                                endDate={appointmentDetails.endDate}
                                                patientFirstName={
                                                   appointmentDetails.patient.firstName?.asString() ?? null
                                                }
                                                patientLastName={
                                                   appointmentDetails.patient.lastName?.asString() ?? null
                                                }
                                             >
                                                <ModalConfirmButton
                                                   label={translationKeys.cancelWithDifferentRefund}
                                                   onClick={noop}
                                                   variant={ButtonVariant.SECONDARY}
                                                />
                                             </RefundAdvancePaymentShowModalEventInterceptor>
                                          </AppointmentCancelActionEventInterceptor>
                                       </AppointmentAdvancePaymentEnableCustomRefundActionEventInterceptor>

                                       <AppointmentCancelActionEventInterceptor
                                          appointmentId={props.appointmentId}
                                          onSuccess={handleAppointmentCancelSuccess(
                                             appointmentCancelSnackbarMessage,
                                             props.onSuccess
                                          )}
                                          onFault={props.onFault}
                                       >
                                          <ModalConfirmButton label={translationKeys.cancel} onClick={noop} />
                                       </AppointmentCancelActionEventInterceptor>
                                    </>
                                 ),
                                 visitWithoutAutomaticRefund: () => (
                                    <>
                                       <AppointmentCancelActionEventInterceptor
                                          appointmentId={props.appointmentId}
                                          onSuccess={handleAppointmentCancelSuccess(
                                             translationKeys.successfullyCancelledPlannedVisit,
                                             props.onSuccess
                                          )}
                                          onFault={props.onFault}
                                       >
                                          <ModalConfirmButton
                                             label={translationKeys.cancel}
                                             onClick={resolve}
                                             variant={ButtonVariant.SECONDARY}
                                          />
                                       </AppointmentCancelActionEventInterceptor>

                                       <AppointmentCancelActionEventInterceptor
                                          appointmentId={props.appointmentId}
                                          onSuccess={handleAppointmentCancelSuccess(
                                             translationKeys.successfullyCancelledPlannedVisit
                                          )}
                                          onFault={props.onFault}
                                       >
                                          <RefundAdvancePaymentShowModalEventInterceptor
                                             advancePayment={advancePayment}
                                             onSuccess={() => handleRefundSuccess(resolve)}
                                             onCancel={() => handleRefundCancel(resolve)}
                                             beginDate={appointmentDetails.beginDate}
                                             endDate={appointmentDetails.endDate}
                                             patientFirstName={appointmentDetails.patient.firstName?.asString() ?? null}
                                             patientLastName={appointmentDetails.patient.lastName?.asString() ?? null}
                                          >
                                             <ModalConfirmButton
                                                label={translationKeys.cancelAndRefund}
                                                onClick={noop}
                                             />
                                          </RefundAdvancePaymentShowModalEventInterceptor>
                                       </AppointmentCancelActionEventInterceptor>
                                    </>
                                 ),
                              }
                           )}
                        </>
                     ),
                     visitCancelUnpaidAdvancePayment: () => (
                        <>
                           <AppointmentAdvancePaymentUnpaidView
                              automaticRefundEnabled={automaticRefundEnabled}
                              appointmentAdvancePaymentRefund={appointmentAdvancePaymentRefund}
                              appointmentDetails={appointmentDetails}
                           />

                           <AppointmentCancelActionEventInterceptor
                              appointmentId={props.appointmentId}
                              onSuccess={handleAppointmentCancelSuccess(
                                 translationKeys.successfullyCancelledPlannedVisit,
                                 props.onSuccess
                              )}
                              onFault={props.onFault}
                           >
                              <ModalConfirmButton label={translationKeys.cancel} onClick={resolve} />
                           </AppointmentCancelActionEventInterceptor>
                        </>
                     ),
                     visitCancel: () => (
                        <>
                           <StyledFlex gap={'20px'}>
                              <AppointmentBeginDateTextView beginDate={appointmentDetails.beginDate} />
                              <AppointmentPatientTextView patient={appointmentDetails.patient} />
                           </StyledFlex>
                           <AppointmentPractitionerTextView practitioner={appointmentDetails.practitioner} />

                           <AppointmentCancelActionEventInterceptor
                              appointmentId={props.appointmentId}
                              onSuccess={handleAppointmentCancelSuccess(
                                 translationKeys.successfullyCancelledPlannedVisit,
                                 props.onSuccess
                              )}
                              onFault={props.onFault}
                           >
                              <ModalConfirmButton label={translationKeys.cancel} onClick={resolve} />
                           </AppointmentCancelActionEventInterceptor>
                        </>
                     ),
                  })}

                  <ModalCloseButton onClick={reject} />
                  <ModalCancelButton onClick={reject} />
               </StyledFlex>
            );
         }}
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
